
import { create } from 'zustand';
import type { ServiceBusMessage } from '@azure/service-bus';
import axios from 'axios';
import request from 'utils/request';
import environment from 'utils/environment';
import replaceByKeyName from 'utils/mockFn';
import { showSnackbar, useUiStore } from 'stores/useUiStore';
import {
  getAuthtoken,
  getSelectedOrganizationId as getAuthSelectedOrganizationId,
} from 'stores/useAuthStore';
import { useCallStore, type CallDetails } from 'stores/useCallStore';
import { USERS_QUERY_KEY } from 'containers/Security/hooks/useSecurity';
import { DEVICES_QUERY_KEY } from 'containers/Devices/hooks/useDevices';
import { queryClient } from 'providers/QueryProvider';
import type { Device } from 'containers/Devices/devices.types';
import type { User } from 'containers/User/user.types';


export type ServiceBusSubscription = {
  success: boolean;
  subscription: string;
  topic: string;
  token: string;
};

export type ServiceBusOnlineStatus = {
  users?: User[];
  devices?: Device['deviceId'][];
};

type RealtimeMessage = Pick<
  ServiceBusMessage,
  'body' | 'replyTo' | 'replyToSessionId' | 'subject'
>;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const parseCallMessageBody = (
  value: unknown
): { action?: unknown; callDetails?: CallDetails } | null => {
  let parsed = value;
  if (typeof parsed === 'string') {
    parsed = JSON.parse(parsed);
  }
  if (!isRecord(parsed)) return null;
  return {
    action: parsed.action,
    callDetails: isRecord(parsed.callDetails) ? parsed.callDetails : undefined,
  };
};

const targetCount = (details: CallDetails | undefined): number | undefined => {
  const targets = details?.targets;
  if (
    !isRecord(targets) ||
    !Array.isArray(targets.users) ||
    !Array.isArray(targets.devices)
  ) {
    return undefined;
  }
  return targets.users.length + targets.devices.length;
};

const getSelectedOrganizationId = () => getAuthSelectedOrganizationId();
const getMaskMode = () => useUiStore.getState().maskMode;

type ActiveConnection = {
  cancelled: boolean;
  close: () => Promise<void>;
};
let activeConnection: ActiveConnection | null = null;

const teardownActiveConnection = async () => {
  const conn = activeConnection;
  activeConnection = null;
  if (conn) {
    conn.cancelled = true;
    try {
      await conn.close();
    } catch (error) {
      console.log('Error closing service bus connection', error);
    }
  }
};

export interface ServiceBusStoreState {
  subscription?: ServiceBusSubscription;
  connected: boolean;
  onlineUpdating: boolean;
  onlineStatus: ServiceBusOnlineStatus;

  provisionSubscription: () => Promise<void>;
  connect: () => Promise<void>;
  refreshOnlineStatus: () => Promise<void>;
  handleMessage: (message: RealtimeMessage) => void;
  reset: () => void;
}

export const useServiceBusStore = create<ServiceBusStoreState>((set, get) => ({
  subscription: undefined,
  connected: false,
  onlineUpdating: false,
  onlineStatus: {},

  provisionSubscription: async () => {
    const authtoken = getAuthtoken();
    console.log('Retrieving new service bus subscription');
    const sb = await request<ServiceBusSubscription>(
      `/api/messaging/provision`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          authorization: authtoken,
        },
      }
    );
    if (!sb) return;
    set({ subscription: sb });
    get().connect();
  },

  connect: async () => {
    const sb = get().subscription;
    if (!sb) return;
    const authtoken = getAuthtoken();

    teardownActiveConnection();

    const [{ ServiceBusClient }, zlibMod] = await Promise.all([
      import('@azure/service-bus'),
      import('zlib'),
    ]);
    const zlib = (zlibMod as { default?: typeof import('zlib') }).default ?? zlibMod;

    let sbErr = false;
    let sbErrCount = 0;
    const maxSbErrCount = 5;

    const client = new ServiceBusClient(
      `Endpoint=sb://${environment.SERVICE_BUS_HOST}/;SharedAccessKeyName=shared-key;SharedAccessKey=${sb.token}`,
      {
        retryOptions: {
          maxRetries: 0,
          timeoutInMs: 6000,
        },
        webSocketOptions: {
          webSocket: WebSocket,
          webSocketConstructorOptions: {},
        },
      }
    );
    const sender = client.createSender(sb.topic);
    const receiver = client.createReceiver(sb.topic, sb.subscription, {
      receiveMode: 'receiveAndDelete',
    });

    const cleanup = async () => {
      await sender.close();
      await receiver.close();
      await client.close();
    };

    const connection: ActiveConnection = { cancelled: false, close: cleanup };
    activeConnection = connection;

    const sendReply = async (
      subject: ServiceBusMessage['subject'],
      body: ServiceBusMessage['body'],
      destination: string | number | boolean | Date | null,
      sessionId: ServiceBusMessage['sessionId']
    ) => {
      try {
        await sender.sendMessages({
          body: zlib.deflateSync(body).toString('base64'),
          applicationProperties: { destination },
          subject,
          sessionId,
        });
      } catch {
        sbErrCount += 1;
        console.log(
          `Error sending service bus reply to api-server, sbErrCount: ${sbErrCount}`
        );
        if (sbErrCount >= maxSbErrCount) {
          cleanup();
          sbErr = true;
        }
      }
    };

    const activateSubscription = () => {
      receiver.subscribe({
        processMessage: async (message) => {
          get().handleMessage(message);
          sendReply(
            'Message successfully received',
            JSON.stringify({ success: true, data: '' }),
            message.replyTo || 'api-server',
            message.replyToSessionId
          );
        },
        processError: async (error) => {
          sbErrCount += 1;
          console.log(
            `General service bus error encountered, sbErrCount: ${sbErrCount}`
          );
          console.log(error);
          if (sbErrCount >= maxSbErrCount) {
            cleanup();
            sbErr = true;
          }
        },
      });
    };

    const delay = async (timeoutSecs: number) =>
      new Promise<void>((resolve) => {
        setTimeout(resolve, timeoutSecs * 1000);
      });

    const sendKeepAlive = async () => {
      let success = false;
      try {
        console.log('Sending keepalive message to harmoni server');
        const result = await axios.post<{ success?: boolean }>(
          '/api/messaging/keepAlive',
          { subscription: sb.subscription },
          {
            headers: {
              'Content-Type': 'application/json',
              authorization: authtoken,
            },
          }
        );
        success = result.data.success ?? false;
      } catch {
        console.log(
          'Error sending keepalive message to harmoni server, trying again in 120 seconds'
        );
      }
      if (!success) {
        console.log(
          'Received error response from keepAlive endpoint, trying again in 120 seconds'
        );
      }
    };

    const waitForConnectionEnd = () =>
      new Promise<void>((resolve) => {
        const scheduleNext = () => {
          delay(120).then(() => {
            if (sbErr || connection.cancelled) {
              resolve();
              return;
            }
            sendKeepAlive().then(scheduleNext);
          });
        };
        scheduleNext();
      });

    const loop = async () => {
      sbErrCount = 0;
      sbErr = false;
      try {
        console.log('Activating service bus');
        activateSubscription();
      } catch {
        console.log('Error activating service bus');
        sbErr = true;
      }
      await waitForConnectionEnd();
      if (connection.cancelled) return;
      console.log(
        `Unrecoverable service bus error(s) occurred, reprovisioning service bus in 120 seconds`
      );
      await delay(120);
      if (connection.cancelled) return;
      get().provisionSubscription();
    };

    loop();

    set({ connected: true });
    get().refreshOnlineStatus();
  },

  refreshOnlineStatus: async () => {
    if (get().onlineUpdating) return;
    set({ onlineUpdating: true });
    const authtoken = getAuthtoken();
    const selectedOrganizationId = getSelectedOrganizationId();
    const userValues =
      queryClient.getQueryData<User[]>([
        ...USERS_QUERY_KEY,
        selectedOrganizationId,
      ]) ?? [];
    const deviceValues =
      queryClient.getQueryData<Device[]>([
        ...DEVICES_QUERY_KEY,
        selectedOrganizationId,
      ]) ?? [];
    console.log('Updating service bus online status');
    try {
      const result = await request<{
        successful: ServiceBusOnlineStatus;
      }>(`/api/messaging/ping`, {
        method: 'POST',
        body: JSON.stringify({
          users: userValues.map((x) => x.userName),
          devices: deviceValues.map((x) => x.deviceId),
        }),
        headers: {
          'Content-Type': 'application/json',
          authorization: authtoken,
        },
      });
      const successful = result?.successful;
      if (getMaskMode() && successful?.users && successful.users.length > 0) {
        successful.users = successful.users.map((x: User) =>
          replaceByKeyName(x, 'userName', 'email')
        );
      }
      set({
        onlineStatus: {
          users: successful?.users,
          devices: successful?.devices,
        },
      });
      console.log('Service bus online status updated');
    } finally {
      set({ onlineUpdating: false });
    }
  },

  handleMessage: (message) => {
    switch (message.subject) {
      case 'ping':
        break;
      case 'notification': {
        const messageText = `Notification message received: ${JSON.stringify(
          message.body
        )}`;
        showSnackbar({
            autoHideDuration: 5000,
            caller: 'onMessageReceived',
            open: true,
            message: messageText,
            severity: 'info',
          });
        break;
      }
      case 'call': {
        const body = parseCallMessageBody(message.body);
        if (!body) break;
        const { callDetails } = body;
        const authtoken = getAuthtoken();
        const callStore = useCallStore.getState();
        const { callOutgoingDetails, callIncomingDetails, callActiveDetails } =
          callStore;
        if (body.action === 'initiate') {
          callStore.setIncoming(body.callDetails);
        } else if (
          body.action === 'sourceAbort' &&
          callDetails?.room === callIncomingDetails?.room
        ) {
          callStore.setIncoming(undefined);
          showSnackbar({
              autoHideDuration: 5000,
              caller: 'onMessageReceived',
              open: true,
              message: 'The caller aborted the call',
              severity: 'info',
            });
        } else if (
          body.action === 'targetAnswer' &&
          callDetails?.room === callOutgoingDetails?.room
        ) {
          callStore.activate(callOutgoingDetails, authtoken);
        } else if (
          body.action === 'targetIgnore' &&
          callDetails?.room === callOutgoingDetails?.room &&
          targetCount(callDetails) === 1
        ) {
          callStore.setOutgoing(undefined);
          showSnackbar({
              autoHideDuration: 5000,
              caller: 'onMessageReceived',
              open: true,
              message: 'The call recipient(s) declined the call',
              severity: 'info',
            });
        } else if (
          body.action === 'targetEnd' &&
          callDetails?.room === callActiveDetails?.room &&
          targetCount(callDetails) === 1
        ) {
          callStore.activate(undefined, authtoken);
          showSnackbar({
              autoHideDuration: 5000,
              caller: 'onMessageReceived',
              open: true,
              message: 'The call participant(s) ended the call',
              severity: 'info',
            });
        }
        break;
      }
      case 'chat':
        console.log(`Chat message received: ${JSON.stringify(message.body)}`);
        break;
      default:
        break;
    }
  },

  reset: () => {
    teardownActiveConnection();
    set({
      subscription: undefined,
      connected: false,
      onlineUpdating: false,
      onlineStatus: {},
    });
  },
}));
